import { h, Component } from 'preact';
import pure from 'utils/pure';


/* TIMED COMPONENT
----------------------------------------------
	WHAT : Hides its children between the startDate, startTime and endDate, endTime ranges.
	WHY : Allows time-specific content to only be displayed when relevant (ie. when showtimes go live, or an offer is valid etc)

	startDate 	: @String (dd/mm/yyyy)
	endDate 	: @String (dd/mm/yyyy)
	startTime 	: @String (hh:mm)
	endTime 	: @String (hh:mm)
	timezone 	: @String (eg. 'GMT', 'BST', 'PST')
	*/
export default @pure class TimedComponent extends Component {


	/* CALCULATE TIMEZONE OFFSET
	----------------------------------------------
		WHAT 	: Looks up the timezone (string) and returns the relative UTC offset in hours (int)
		WHY 	: Used when calculating how many hours we need to adjust the startTime and endTime by for users in other timezones

		timezone : @String (eg. 'GMT', 'PST' etc)
		*/
	calculateTimezoneOffset = (timezone) => {
		if (timezone) {
			// TODO: There are a lot of duplicated keys in this switch.
			// Also, some timezones are wrong (e.g. IST -> +1/+2 ???)
			/* eslint-disable no-duplicate-case */
			switch (timezone) {
				case 'GMT':
				case 'AZOST':
				case 'EGST':
				case 'UTC':
				case 'WET':
					return 0;

				case 'BST':
				case 'CET':
				case 'IST':
				case 'MET':
				case 'WAT':
				case 'WEST':
					return 1;

				case 'CAT':
				case 'CEST':
				case 'EET':
				case 'IST':
				case 'MEST':
				case 'SAST':
				case 'WAST':
					return 2;

				case 'AST':
				case 'EAT':
				case 'EEST':
				case 'FET':
				case 'IDT':
				case 'IOT':
				case 'MSK':
				case 'TRT':
					return 3;

				case 'AMT':
				case 'AZT':
				case 'GET':
				case 'GST':
				case 'MUT':
				case 'RET':
				case 'SAMT':
				case 'SCT':
				case 'VOLT':
					return 4;

				case 'HMT':
				case 'MAWT':
				case 'MVT':
				case 'ORAT':
				case 'PKT':
				case 'TFT':
				case 'TJT':
				case 'TMT':
				case 'UZT':
				case 'YEKT':
					return 5;

				case 'BIOT':
				case 'BTT':
				case 'KGT':
				case 'KGT':
				case 'OMST':
				case 'VOST':
					return 6;

				case 'CXT':
				case 'DAVT':
				case 'HOVT':
				case 'ICT':
				case 'KRAT':
				case 'THA':
				case 'WIT':
					return 7;

				case 'AWST':
				case 'BDT':
				case 'CHOT':
				case 'CIT':
				case 'CST':
				case 'CT':
				case 'HKT':
				case 'HOVST':
				case 'IRKT':
				case 'MST':
				case 'MYT':
				case 'PHT':
				case 'PST':
				case 'SGT':
				case 'SST':
				case 'ULAT':
				case 'WST':
					return 8;

				case 'CHOST':
				case 'EIT':
				case 'JST':
				case 'KST':
				case 'TLT':
				case 'ULAST':
				case 'YAKT':
					return 9;

				case 'AEST':
				case 'CHST':
				case 'CHUT':
				case 'DDUT':
				case 'PGT':
				case 'VLAT':
					return 10;

				case 'AEDT':
				case 'KOST':
				case 'LHST':
				case 'MIST':
				case 'NCT':
				case 'NFT':
				case 'PONT':
				case 'SAKT':
				case 'SBT':
				case 'SRET':
				case 'VUT':
					return 11;

				case 'FJT':
				case 'GILT':
				case 'MAGT':
				case 'MHT':
				case 'NZST':
				case 'PETT':
				case 'TVT':
				case 'WAKT':
					return 12;

				case 'NZDT':
				case 'PHOT':
				case 'TKT':
				case 'TOT':
					return 13;

				case 'LINT':
					return 14;

				case 'AZOT':
				case 'CVT':
				case 'EGT':
					return -1;

				case 'BRST':
				case 'FNT':
				case 'GST':
				case 'PMDT':
				case 'UYST':
					return -2;

				case 'ADT':
				case 'AMST':
				case 'ART':
				case 'BRT':
				case 'CLST':
				case 'FKST':
				case 'GFT':
				case 'PMST':
				case 'PYST':
				case 'ROTT':
				case 'SRT':
				case 'UYT':
					return -3;

				case 'AMT':
				case 'AST':
				case 'BOT':
				case 'CDT':
				case 'CLT':
				case 'COST':
				case 'ECT':
				case 'EDT':
				case 'FKT':
				case 'GYT':
				case 'PYT':
				case 'VET':
					return -4;

				case 'ACT':
				case 'CDT':
				case 'COT':
				case 'CST':
				case 'EASST':
				case 'ECT':
				case 'EST':
				case 'PET':
					return -5;

				case 'CST':
				case 'EAST':
				case 'GALT':
				case 'MDT':
					return -6;

				case 'MST':
				case 'PDT':
					return -7;

				case 'AKDT':
				case 'CIST':
				case 'PST':
					return -8;

				case 'AKST':
				case 'GAMT':
				case 'GIT':
				case 'HADT':
					return -9;

				case 'CKT':
				case 'HAST':
				case 'TAHT':
					return -10;

				case 'NUT':
				case 'SST':
					return -11;

				case 'BIT':
					return -12;

				default :
					console.warn(`WARNING : unknown timezone ${timezone} used in timedComponent, using GMT as default`);
					return 0;
			}
			/* eslint-enable no-duplicate-case */
		} else return 0;
	}//calculateTimezoneOffset

	/* ADJUST FOR TIMEZONE
	----------------------------------------------
		WHAT 	: Adds the given amount of hours in offset (int) to the date (Date) based on the user's own timeline
		WHY 	: To ensure that the date used is based on a fixed absolute time, as opposed to whatever time '6pm' is in the users own individual timezones

		date 	: @Date (object)
		offset 	: @Int
		*/
	adjustForTimezone = (date, offset) => {
		const adjustedTime = date.setHours(date.getHours() + offset);
		const adjustedDate = new Date(adjustedTime);
		return adjustedDate;
	}//adjustForTimezone

	render(props) {

		let {
			startDate,
			startTime = '00:00',
			endDate,
			endTime = '00:00',
			timezone,
			children
		} = props;

		const [startDay, startMonth, startYear] = startDate ? startDate.split('/') : [false, false, false];
		const [endDay, endMonth, endYear] = endDate ? endDate.split('/') : [false, false, false];
		let start = startDate ? new Date(`${startMonth}/${startDay}/${startYear} ${startTime}:00`) : false;
		let end = endDate ? new Date(`${endMonth}/${endDay}/${endYear} ${endTime}:00`) : false;
		const currentTime = new Date();
		const timezoneOffset = this.calculateTimezoneOffset(timezone) - currentTime.getTimezoneOffset() / 60;
		let renderComponent = false;


		// if a timezone offset needs to be applied
		if (timezoneOffset !== 0) {
			const difference = timezoneOffset - (currentTime.getTimezoneOffset() / 60);
			start = start ? this.adjustForTimezone(start, difference) : false;
			end = end ? this.adjustForTimezone(end, difference) : false;
		}

		//if the element is set to show after a particular date...
		if (start) {
			//if the current time is after the start trigger
			if (currentTime > start) {
				//if an end-trigger is specified...
				if (end) {
					//if the current time is between the start and end triggers, then render the component
					if (currentTime < end) renderComponent = true;
				}
				//if the current time is after the start trigger and there's no end trigger, then render the component
				else renderComponent = true;
			}
		}
		//otherwise if the element is set to show until a particular date...
		else if (end) {
			//if the current time is before the end trigger then render the component (we have already checked start-time so no need to check again)
			if (currentTime < end) renderComponent = true;
		}
		//if no start time or end time has been supplied but it's still been wrapped in this component for some reason, render it anyway
		else renderComponent = true;

		if (renderComponent) return children[0];
		else return '';
	}//render

}//TimedComponent
